﻿import { QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ALL_SCREENS_QUERY_KEY, SCREEN_API_URL } from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { CreateScreenPayload, ErrorResponse, Options, UpdateScreenNamePayload } from '@/types';

import { useMutation } from './service';

export const screenEndpoints = {
  getAllScreens: (pageSize: number, cursor: number, searchQuery?: string, deviceId?: number) =>
    axios.get(
      `${apiUrl()}${SCREEN_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.DeviceId': deviceId as number
      })}`
    ),
  createScreen: (payload: CreateScreenPayload) =>
    axios.post(`${apiUrl()}${SCREEN_API_URL}`, payload),
  updateScreenName: (payload: UpdateScreenNamePayload) =>
    axios.patch(`${apiUrl()}${SCREEN_API_URL}`, payload),
  deleteScreen: (id: number) => axios.delete(`${apiUrl()}${SCREEN_API_URL}/${id}`)
};

export const useGetAllScreens = (
  pageSize: number,
  searchQuery?: string,
  deviceId?: number,
  enabled = true
) => {
  return useInfiniteQuery(
    [ALL_SCREENS_QUERY_KEY, { pageSize, searchQuery, deviceId }],
    ({ pageParam }) => screenEndpoints.getAllScreens(pageSize, pageParam, searchQuery, deviceId),
    {
      keepPreviousData: true,
      enabled,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      cacheTime: 1000
    }
  );
};

export const useCreateScreenMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(screenEndpoints.createScreen, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_SCREENS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateScreenNameMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(screenEndpoints.updateScreenName, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_SCREENS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteScreenMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(screenEndpoints.deleteScreen, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_SCREENS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
